import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Button, MenuItem, TextField, Typography, FormControlLabel } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from '@mui/material/Modal';
import { FormInputTextArea } from '../../../form-components/FormInputTextArea';
import { useForm } from 'react-hook-form';
import { getMemberByBranch, getMemberByTicket } from '../../../api/member-management';
import { searchTickets } from '../../../api/ticket';
import { getBranchManager } from '../../../api/staff';
import { Close } from '@mui/icons-material';
import QueryString from 'qs';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import { getTicketById } from '../../../api/ticket';
import { FormInputText } from '../../../form-components/FormInputText';
import { createProductList } from '../../../api/payment';
import { getBranchProductsById, getHeadBranchId } from '../../../api/point';
import CustomRadioBox from '../../CustomRadioBox';
import { FormInputDate } from '../../../form-components/FormInputDate';
import CustomCheckbox from '../../CustomCheckbox';
import dayjs from 'dayjs';
import moment from 'moment';

import CardPaymentForm from '../payment/cardPaymentForm';
import CashPaymentForm from '../payment/cashPaymentForm';
import BankTransferForm from '../payment/bankPaymentForm';
import { getPointTrainers } from '../../../api/staff';

const payMethods = [
  { value: '카드', label: '카드' },
  { value: '계좌이체', label: '계좌이체' },
  { value: '현금', label: '현금' },
];

const ProductModalV2 = ({ fetchData }) => {
  // 최상위 모달 스타일 수정
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '507px',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
  };
  const [searchInput, setSearchInput] = useState('');

  const toast = useToast();
  const { user } = useSelector(state => state.user);
  const [searchResults, setSearchResults] = useState([]);
  const [memberData, setMemberData] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [branchManager, setBranchManager] = useState(null);
  const [product, setProduct] = useState(null);
  const [stockQuantity, setStockQuantity] = useState([]);
  const [ticketPrice, setTicketPrice] = useState([]);
  const [ticketDiscountAmount, setTicketDiscountAmount] = useState('');
  const [ticketFinalPrice, setTicketFinalPrice] = useState('');
  const [ticketOutstandingPrice, setTicketOutstandingPrice] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(user.branch?.branchId ? user.branch.branchId : null);
  const [trainers, setTrainers] = useState(null); // 강사 목록
  const [selectedTicketType, setSelectedTicketType] = useState(null); // 선택된 수강권 타입
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      searchInput: '',
      paymentMethod: null,
      branchManager: null,
      memo: '',
      courseStartDate: '',
      cardCompanyCode: null, // 추가
      cardNumber: '', // 추가
      approvalNumber: '', // 추가
      // 추가할 필드들
      receiptYn: 'Y',
      deductionType: 0,
      receiptType: 'phone',
      personalId: '',
      businessNumber: '',
      bankInfo: '',
      depositorName: '',
      trainers: '', // 담당 강사 초기화
    },
  });

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    // branchId 체크 추가
    if (!user.branch?.branchId) {
      toast.error('지점 정보가 없습니다');
      return;
    }

    setOpen(true);
  };

  const handleClose = (e, r) => {
    if (r === 'backdropClick') {
      return; // 백드롭 클릭으로 닫히는 것을 방지
    }

    setStockQuantity(0);
    setTicketPrice([]);
    setTicketDiscountAmount('');
    setTicketFinalPrice('');
    setTicketOutstandingPrice('');
    setSelectedTicketType(null); // 수강권 타입 초기화
    reset({
      paymentMethod: null,
      branchManager: null,
      memo: '',
      courseStartDate: '',
      trainers: '', // 담당 강사 초기화
    });

    setOpen(false);
  };

  const submit = async values => {
    if (!values.product) {
      return setError('product', {
        type: 'custom',
        message: 'Please select a course ticket',
      });
    }

    try {
      const request = {
        userId: memberData.userId,
        memberId: memberData.memberId,
        // userType: userInfo.userType,
        courseType: 'PRODUCT',
        productId: values.product,
        // #73 다지점 공유 수강권 — 판매지점 귀속(선택 지점, 없으면 로그인 지점)
        branchId: selectedBranch || user.branch?.branchId,
        paymentMethod: values.paymentMethod,
        registeredBy: values.branchManager,
        courseStartDate: values.courseStartDate,
        ticketDiscountAmount: Number(values.ticketDiscountAmount.toString().replace(/,/g, '')),
        ticketFinalPrice: Number(values.ticketFinalPrice.toString().replace(/,/g, '')),
        ticketOutstandingPrice: Number(values.ticketOutstandingPrice.toString().replace(/,/g, '')),
        stockQuantity: values.stockQuantity,
        memo: values.memo,
        // 카드 결제 정보 추가
        ...(values.paymentMethod === '카드' && {
          installmentMonth: values.installmentMonth || null,
          cardCompanyCode: values.cardCompanyCode || null,
          cardNumber: values.cardNumber || null,
          cId: values.approvalNumber || null,
        }),

        // 계좌이체 결제 시 추가되는 정보
        ...(values.paymentMethod === '계좌이체' && {
          // 현금영수증 발행 여부에 따른 처리
          ...(values.receiptYn === 'Y' && {
            // 개인/사업자 구분에 따른 처리
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            // 식별번호 (개인: 휴대폰/주민번호, 사업자: 사업자등록번호)
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            // 식별 타입 (개인: phone/resident, 사업자: BUSINESS)
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS' || null,
          }),
          // 입금 정보
          receiptYn: values.receiptYn || null,
          bankInfo: values.bankInfo || null,
          depositorName: values.depositorName || null,
          cId: values.approvalNumber || null,
        }),

        // 현금 결제 시 추가되는 정보
        ...(values.paymentMethod === '현금' && {
          // 현금영수증 발행 여부에 따른 처리
          ...(values.receiptYn === 'Y' && {
            // 개인/사업자 구분에 따른 처리
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            // 식별번호 (개인: 휴대폰/주민번호, 사업자: 사업자등록번호)
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            // 식별 타입 (개인: phone/resident, 사업자: BUSINESS)
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS' || null,
          }),
          // 입금 정보
          receiptYn: values.receiptYn || null,
          cId: values.approvalNumber || null,
        }),
      };

      await createProductList(request, user.token);

      await fetchData();

      toast.success('등록되었습니다');
      handleClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //판매 담당자 조회
  const getManager = async branch => {
    try {
      const data = await getBranchManager(branch, user.token);
      const branchManagerArray = data.map(item => ({
        value: item.managerId,
        label: item.managerName,
      }));
      setBranchManager(branchManagerArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //수강권 조회
  const getProduct = async branch => {
    const query = {
      page: 1,
      limit: 1000000,
    };

    try {
      const qstring = QueryString.stringify(query);
      const data = await getBranchProductsById(user.token, user.branch?.branchId, qstring);

      const productArray = data?.content.map(item => ({
        value: item.productId,
        label: item.productName,
        price: item.price,
        productId: item.productId,
        stockQuantity: item.stockQuantity,
      }));
      setProduct(productArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //지점 조회
  const getBranches = async () => {
    try {
      const data = await getHeadBranchId(user.token, user.headBranch?.headBranchId);
      setBranches(data);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  //수강권 정보 및 가격 조회
  const getPrice = id => {
    try {
      const selectedProduct = product.find(item => item.productId === id);

      if (selectedProduct) {
        let discountAmount = 0;
        setStockQuantity(selectedProduct.stockQuantity);
        setTicketPrice(selectedProduct.price);
        setTicketDiscountAmount(discountAmount);
        setTicketFinalPrice(selectedProduct.price);
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleBranchSelect = e => {
    setValue('branchId', e.target.value);
    setSelectedBranch(e.target.value);
  };

  function changeTicketFinalPrice(value) {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    let outPrice = parseInt(String(ticketOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;

    let changeValue = ticketPrice - (value + outPrice);
    setTicketDiscountAmount(value);
    setTicketFinalPrice(changeValue);
  }

  // ticketOutstandingPrice 변경 함수 추가
  function changeTicketOutstandingPrice(value) {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    let disPrice = parseInt(String(ticketDiscountAmount || 0).replace(/[^\d]/g, '')) || 0;

    let changeValue = ticketPrice - (value + disPrice);
    setTicketOutstandingPrice(value);
    setTicketFinalPrice(changeValue);
  }

  function formatInteger(value) {
    if (!value && value !== 0) {
      return '0';
    }

    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 회원 검색 함수 추가
  const getMembers = async inputValue => {
    if (!inputValue || inputValue.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const qstring = QueryString.stringify({
        searchInput: inputValue,
        headBranchId: user.headBranch?.headBranchId,
        branchId: user.branch?.branchId,
      });

      const data = await getMemberByBranch(user.token, qstring);
      const searchResultsArray = data?.content.map(item => ({
        id: item.memberId,
        name: item.name,
        memberData: item,
      }));
      setSearchResults(searchResultsArray);
    } catch (error) {
      toast.error(errorMessages(error));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 회원 선택 핸들러
  const handleMemberSelect = (event, value) => {
    if (value) {
      setMemberData(value.memberData);
      setMemberId(value.memberData.memberId);
    } else {
      setMemberData(null);
      setMemberId(null);
    }
  };

  useEffect(() => {
    if (user.headBranch?.branchId) {
      getBranches();
    }

    if (user.branch?.branchId) {
      //상품조회
      getProduct();
    }
  }, [user.headBranch, user.branch]);

  // useEffect(() => {
  //   if (selectedBranch) {
  //     getProduct(selectedBranch);
  //     getManager(selectedBranch);
  //   }
  // }, [selectedBranch]);

  useEffect(() => {
    setValue('stockQuantity', stockQuantity);
    setValue('ticketFinalPrice', formatInteger(ticketFinalPrice));
    setValue('ticketDiscountAmount', formatInteger(ticketDiscountAmount));
    setValue('ticketOutstandingPrice', formatInteger(ticketOutstandingPrice));
  }, [ticketFinalPrice, ticketDiscountAmount, stockQuantity, ticketOutstandingPrice]);

  return (
    <div style={{ display: 'inline-block' }}>
      {/* <Button
        sx={{
          background: '#0F7F85',
          '&:hover': { backgroundColor: '#0F7F85' },
          borderRadius: '10px',
          marginRight: '20px',
        }}
        onClick={handleOpen}
        variant="contained">
        {t('Sales Registration')}
      </Button> */}

      <Modal
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown // ESC 키로 닫히는 것을 방지
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={modalStyle}>
          {/* 헤더 영역 */}
          <Typography
            id="modal-modal-title"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: '20px 30px',
              borderBottom: '1px solid #e1e1e1',
            }}>
            <Box sx={{ fontSize: '18px' }}>{t('Enter Sales Information')}</Box>
            <button
              onClick={handleClose}
              style={{
                color: 'black',
                width: '40px',
                height: '40px',
                padding: '0px 0px 0px 0px',
                border: '1px solid white',
                backgroundColor: 'white',
              }}>
              <Close onChange={handleClose} sx={{ width: '20px', height: '20px' }}></Close>
            </button>
          </Typography>

          {/* 컨텐츠 영역 */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: '20px 30px',
            }}>
            <Typography
              id="modal-modal-description"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
              <Box
                id="modal-modal-body"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#555',
                    },
                  },
                }}>
                {/* 회원 검색 추가 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}>
                  <Typography sx={{ fontSize: '14px', mr: '25px' }}>{t('Member Search')}</Typography>
                  <Autocomplete
                    control={control}
                    sx={{
                      width: '280px',
                      height: '30px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px', // FormInputDropdown과 동일한 높이
                        padding: '0 9px',
                        borderRadius: '8px',
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '7.5px 4px 7.5px 6px !important',
                      },
                    }}
                    onChange={handleMemberSelect}
                    disablePortal
                    getOptionLabel={option => option?.name}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    options={searchResults}
                    renderInput={params => (
                      <TextField
                        sx={{
                          fontSize: '14px',
                          '& .MuiOutlinedInput-root': {
                            padding: 0,
                            borderRadius: '8px',
                          },
                        }}
                        placeholder="회원을 검색해주세요."
                        name="searchInput"
                        onInput={e => getMembers(e.target.value)}
                        {...params}
                        label=""
                      />
                    )}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '10px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Select Product')}
                    </Typography>
                    <FormInputDropdown
                      control={control}
                      onChange={e => getPrice(e.target.value)}
                      sx={{
                        width: '280px',
                        '& .MuiInputBase-root': {
                          border: '1px solid #E0E0E0',
                          borderRadius: '10px',
                        },
                      }}
                      rules={{ required: true }}
                      name="product"
                      options={product}
                      disabledItem={<MenuItem disabled>{t('Select Product')}</MenuItem>}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '20px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Product Amount')}
                    </Typography>

                    <Box
                      sx={{
                        width: '280px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <FormInputText
                        control={control}
                        disabled
                        onMouseOver={e => (e.target.style.cursor = 'pointer')}
                        color="info"
                        sx={{
                          width: '237px',
                          height: '30px',
                          '& .MuiInputBase-root': {
                            borderRadius: '8px',
                          },
                        }}
                        variant="outlined"
                        name="searchInp"
                        size="small"
                        staticValue={formatInteger(ticketPrice)}
                        placeholder={t('Automatically generated when selecting a product')}></FormInputText>
                      <Typography sx={{ paddingTop: '10px', paddingRight: '7px', fontSize: '10px' }}>
                        {t('₩')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '20px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Discounted Price')}
                    </Typography>

                    <Box
                      sx={{
                        width: '280px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <FormInputText
                        control={control}
                        onMouseOver={e => (e.target.style.cursor = 'pointer')}
                        onChange={e => changeTicketFinalPrice(e.target.value)}
                        color="info"
                        sx={{
                          width: '237px',
                          height: '30px',
                          '& .MuiInputBase-root': {
                            borderRadius: '8px',
                          },
                        }}
                        variant="outlined"
                        name="ticketDiscountAmount"
                        size="small"
                        value={ticketDiscountAmount}
                        placeholder={t('Automatically generated when selecting a ticket')}></FormInputText>
                      <Typography sx={{ paddingTop: '10px', paddingRight: '7px', fontSize: '10px' }}>
                        {t('₩')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '20px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Outstanding Amount')}
                    </Typography>

                    <Box
                      sx={{
                        width: '280px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <FormInputText
                        control={control}
                        onMouseOver={e => (e.target.style.cursor = 'pointer')}
                        onChange={e => changeTicketOutstandingPrice(e.target.value)}
                        color="info"
                        sx={{
                          width: '237px',
                          height: '30px',
                          '& .MuiInputBase-root': {
                            borderRadius: '8px',
                          },
                        }}
                        variant="outlined"
                        name="ticketOutstandingPrice"
                        size="small"
                        value={formatInteger(ticketOutstandingPrice)}
                        placeholder={t('Please Enter The Outstanding Amount')}></FormInputText>
                      <Typography sx={{ paddingTop: '10px', paddingRight: '7px', fontSize: '10px' }}>
                        {t('₩')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '20px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Amount of payment')}
                    </Typography>

                    <Box
                      sx={{
                        width: '280px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <FormInputText
                        control={control}
                        onMouseOver={e => (e.target.style.cursor = 'pointer')}
                        color="info"
                        disabled={true}
                        sx={{
                          width: '237px',
                          height: '30px',
                          '& .MuiInputBase-root': {
                            borderRadius: '8px',
                          },
                        }}
                        variant="outlined"
                        name="ticketFinalPrice"
                        size="small"
                        value={formatInteger(ticketFinalPrice)}
                        placeholder={t('Automatically generated when selecting a ticket')}></FormInputText>
                      <Typography sx={{ paddingTop: '10px', paddingRight: '7px', fontSize: '10px' }}>
                        {t('₩')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '20px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Inventory')}
                    </Typography>

                    <Box
                      sx={{
                        width: '280px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <FormInputText
                        control={control}
                        onMouseOver={e => (e.target.style.cursor = 'pointer')}
                        color="info"
                        sx={{
                          width: '237px',
                          height: '30px',
                          '& .MuiInputBase-root': {
                            borderRadius: '8px',
                          },
                        }}
                        variant="outlined"
                        name="stockQuantity"
                        size="small"
                        disabled={true}
                        value={stockQuantity}
                        placeholder={t('Automatically generated when selecting a product')}></FormInputText>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '20px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Payment Method')}
                    </Typography>
                    <FormInputDropdown
                      control={control}
                      sx={{
                        width: '280px',
                        '& .MuiInputBase-root': {
                          border: '1px solid #E0E0E0',
                          borderRadius: '10px',
                        },
                      }}
                      name="paymentMethod"
                      options={payMethods}
                      disabledItem={<MenuItem disabled>{t('Select Payment Method')}</MenuItem>}
                    />
                  </Box>

                  {/* 결제 방법별 추가 폼 */}
                  {watch('paymentMethod') === '카드' && <CardPaymentForm control={control} setValue={setValue} />}
                  {watch('paymentMethod') === '현금' && <CashPaymentForm control={control} setValue={setValue} />}
                  {watch('paymentMethod') === '계좌이체' && <BankTransferForm control={control} setValue={setValue} />}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: '10px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography className="title" sx={{ fontSize: '14px', mr: '25px' }}>
                      {t('Sale Manager')}
                    </Typography>
                    <FormInputDropdown
                      control={control}
                      sx={{
                        width: '280px',
                        '& .MuiInputBase-root': {
                          border: '1px solid #E0E0E0',
                          borderRadius: '10px',
                        },
                      }}
                      rules={{ required: true }}
                      name="branchManager"
                      options={branchManager}
                      disabledItem={<MenuItem disabled>{t('Select Branch Manager')}</MenuItem>}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      mb: '10px',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}>
                    <Typography sx={{ mr: '25px' }}>{t('Memo')}</Typography>
                    <FormInputTextArea
                      control={control}
                      minRows={8}
                      style={{
                        width: '280px',
                        height: '110px',
                        padding: '12px 13px 10px 11px',
                        fontSize: '13px',
                        fontWeight: '500',
                        border: '1px solid #AEAEAEB5',
                        borderRadius: '8px',
                        color: '#1f2329 !important',
                      }}
                      placeholder={t('Please Enter a Note')}
                      name="memo"
                      rules={{ required: true }}
                    />
                  </Box>
                </Box>
              </Box>
            </Typography>
          </Box>

          {/* 푸터 영역 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: '20px 30px',
              borderTop: '1px solid #e1e1e1',
            }}>
            <Button
              onClick={handleSubmit(submit)}
              variant="contained"
              sx={{
                background: '#0F7F85',
                '&:hover': { backgroundColor: '#0F7F85' },
                borderRadius: '10px',
                width: '120px',
                height: '40px',
              }}>
              {t('Sales Registration')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ProductModalV2;
